import React from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    InputAdornment,
    Stack,
    Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import CommonInput from '../../../component/form/common-input.component';
import i18n from '../../../i18n';
import {
    COUNTRY_REG_CITY_REGULAR,
    COUNTRY_REG_POSTCODE_REGULAR,
} from '../../../constants/regular.constant';
import { SPAIN_CITY_AREA } from '../../../constants/spain-city-area';
import type { RestaurantInfoState } from './restaurant-info-state';
import {
    getRestaurantAddressDifferences,
    type RestaurantAddressCopyDirection,
} from './restaurant-address-sync';
import ButtonComponent from '../../../component/button/button.component';

/** 双地址组件只依赖的餐厅状态接口。 */
type RestaurantDualAddressState = Pick<
    RestaurantInfoState,
    | 'componentType'
    | 'editRestaurantData'
    | 'isAddressSame'
    | 'updateEditRestaurantData'
    | 'setAddressSame'
    | 'copyAddressGroup'
    | 'getLanguage'
    | 'searchMunicipalityByPostcode'
>;

/** 餐厅双地址组件属性。 */
export interface RestaurantDualAddressProps {
    /** 餐厅表单状态，保存逻辑仍由原状态类负责。 */
    restaurantInfoState: RestaurantDualAddressState;
    /** Google Maps 脚本是否已加载完成，供营业地址 GOOGLE_PLACE 初始化预测服务。 */
    googleMapsLoaded?: boolean;
}

/** 单个地址输入字段定义。 */
interface AddressFieldDefinition {
    /** 餐厅实体字段名。 */
    key: string;
    /** 输入框标签 i18n key。 */
    label: string;
    /** CommonInput 输入形态。 */
    inputType?: 'INPUT' | 'GOOGLE_PLACE' | 'SELECT';
    /** 是否属于四个同步配对字段。 */
    paired?: boolean;
    /** 列内栅格宽度：12 整行，6 半行。 */
    md?: number;
}

/** 营业地址字段，顺序与税务地址对应行保持一致。 */
const BUSINESS_ADDRESS_FIELDS: AddressFieldDefinition[] = [
    {
        key: 'local',
        label: 'restaurant_info_page_text_restaurant_address',
        inputType: 'GOOGLE_PLACE',
        paired: true,
        md: 12,
    },
    { key: 'home_umber', label: 'restaurant_info_page_text_home_number', md: 12 },
    { key: 'postcode', label: 'restaurant_info_page_text_postcode', paired: true, md: 6 },
    { key: 'city', label: 'restaurant_info_page_text_city', paired: true, md: 6 },
    { key: 'state', label: 'restaurant_info_page_text_state', paired: true, md: 6 },
    { key: 'country', label: 'restaurant_info_page_text_country', md: 6 },
];

/** 税务地址基础字段，西班牙专属字段在组件内按国家追加。 */
const TAX_ADDRESS_FIELDS: AddressFieldDefinition[] = [
    { key: 'reg_local', label: 'address_component_text_address', paired: true, md: 12 },
    { key: 'reg_additional_address', label: 'restaurant_info_page_text_additional_address', md: 12 },
    { key: 'reg_postcode', label: 'restaurant_info_page_text_postcode', paired: true, md: 6 },
    { key: 'reg_city', label: 'restaurant_info_page_text_city', paired: true, md: 6 },
    { key: 'reg_state', label: 'restaurant_info_page_text_state', paired: true, md: 6 },
    { key: 'reg_country', label: 'restaurant_info_page_text_country', md: 6 },
];

/**
 * 返回税务地址字段当前国家对应的既有正则名。
 * @param key 税务地址字段名
 * @param countryCode 国家电话区号
 * @returns 正则名；无需校验时返回 undefined
 */
const getAddressRegularType = (
    key: string,
    countryCode: string,
): string | undefined => {
    if (key === 'reg_postcode') {
        return COUNTRY_REG_POSTCODE_REGULAR[countryCode];
    }
    if (['reg_local', 'reg_city'].includes(key)) {
        return COUNTRY_REG_CITY_REGULAR[countryCode];
    }
    if (['reg_state', 'reg_country'].includes(key)) {
        return 'regName';
    }
    return undefined;
};

/**
 * 将地址字段值安全转为 CommonInput 可展示的文本。
 * @param value 餐厅实体中的未知字段值
 * @returns 字符串或数字原值，其他结构返回空字符串
 */
const getAddressInputValue = (value: unknown): string | number => (
    typeof value === 'string' || typeof value === 'number' ? value : ''
);

/**
 * 渲染餐厅营业地址和税务地址，并复用状态层的复制、同步与差异逻辑。
 * @param props 餐厅双地址状态
 * @returns 双地址并排表单
 */
export function RestaurantDualAddress({
    restaurantInfoState,
    googleMapsLoaded,
}: Readonly<RestaurantDualAddressProps>) {
    /** 页面是否完全只读。 */
    const isPreview = restaurantInfoState.componentType === 'PREVIEW';
    /** 当前是否为西班牙餐厅。 */
    const isSpain = restaurantInfoState.editRestaurantData.country_code === '+34';
    /** 未锁定时的四项地址差异。 */
    const differences = restaurantInfoState.isAddressSame
        ? []
        : getRestaurantAddressDifferences(restaurantInfoState.editRestaurantData);

    /**
     * 写入普通地址字段。
     * @param key 餐厅字段名
     * @param value 新输入值
     */
    const updateField = (key: string, value: string) => {
        restaurantInfoState.updateEditRestaurantData(value, key);
    };

    /**
     * 处理 Google Place 回填，并由状态层继续同步税务侧配对字段。
     * @param place Google 地址选择结果
     */
    const handleGooglePlace = (place: {
        /** 完整街道地址。 */
        value?: string;
        /** 邮编。 */
        postalCode?: string;
        /** 城市。 */
        city?: string;
        /** 省或州。 */
        state?: string;
    }) => {
        updateField('local', place.value || '');
        updateField('postcode', place.postalCode || '');
        updateField('city', place.city || '');
        updateField('state', place.state || '');
    };

    /**
     * 按固定方向复制四个配对字段。
     * @param direction 复制方向
     */
    const copyAddress = (direction: RestaurantAddressCopyDirection) => {
        restaurantInfoState.copyAddressGroup(direction);
    };

    /**
     * 渲染一侧地址字段。
     * @param field 地址字段定义
     * @param taxSide 是否为税务地址侧
     * @returns CommonInput 地址控件
     */
    const renderAddressField = (
        field: AddressFieldDefinition,
        taxSide: boolean,
    ): React.ReactNode => {
        /** 国家字段始终由所在国家跟随并只读。 */
        const isCountry = field.key === 'country' || field.key === 'reg_country';
        /** 地址同步时仅锁定税务侧四个配对字段。 */
        const disabled = isPreview
            || isCountry
            || (taxSide && Boolean(field.paired) && restaurantInfoState.isAddressSame);
        /** 税务侧非空输入沿用当前国家的既有格式规则。 */
        const regularType = getAddressRegularType(
            field.key,
            restaurantInfoState.editRestaurantData.country_code || '',
        );
        /** 当前税务字段是否与营业侧配对值不同。 */
        const isDifferent = taxSide && differences.some((pair) => pair.tax === field.key);
        return (
            <CommonInput
                key={field.key}
                inputType={field.inputType ?? 'INPUT'}
                fullWidth
                label={field.label}
                size="small"
                disabled={disabled}
                regularType={regularType}
                className={isDifferent ? 'restaurant-dual-address__field--different' : undefined}
                value={getAddressInputValue(
                    restaurantInfoState.editRestaurantData[
                    field.key as keyof typeof restaurantInfoState.editRestaurantData
                    ],
                )}
                language={field.inputType === 'GOOGLE_PLACE'
                    ? restaurantInfoState.getLanguage()
                    : undefined}
                onChange={(event) => updateField(field.key, event.target.value)}
                handleChangeGooglePlace={field.inputType === 'GOOGLE_PLACE'
                    ? handleGooglePlace
                    : undefined}
                googleMapsLoaded={field.inputType === 'GOOGLE_PLACE'
                    ? googleMapsLoaded
                    : undefined}
            />
        );
    };

    /**
     * 渲染一侧地址列（标题、说明、复制按钮与字段网格）。
     * @param side 营业或税务列
     * @returns 地址列卡片
     */
    const renderAddressColumn = (side: 'business' | 'tax'): React.ReactNode => {
        /** 是否为税务地址列。 */
        const isTax = side === 'tax';
        /** 同步开启后税务列进入跟随锁定态。 */
        const locked = isTax && restaurantInfoState.isAddressSame;
        /** 当前列使用的字段定义。 */
        const fields = isTax ? TAX_ADDRESS_FIELDS : BUSINESS_ADDRESS_FIELDS;
        return (
            <Box
                className={`restaurant-dual-address__column${locked ? ' restaurant-dual-address__column--locked' : ''}`}
            >
                <Stack
                    className="restaurant-dual-address__column-header"
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    spacing={1}
                >
                    <Typography variant="subtitle2">
                        {i18n.t(isTax ? 'archive_form_tax_address' : 'archive_form_business_address')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {i18n.t(isTax ? 'archive_form_tax_address_sub' : 'archive_form_business_address_sub')}
                    </Typography>
                    {locked ? (
                        <Typography
                            className="restaurant-dual-address__follow"
                            variant="caption"
                            color="text.secondary"
                        >
                            {i18n.t('archive_form_address_follow_left')}
                        </Typography>
                    ) : (
                        <Button
                            className="restaurant-dual-address__copy"
                            variant="outlined"
                            size="small"
                            disabled={isPreview}
                            onClick={() => copyAddress(isTax ? 'TAX_TO_BUSINESS' : 'BUSINESS_TO_TAX')}
                        >
                            {i18n.t(isTax ? 'archive_form_copy_to_business' : 'archive_form_copy_to_tax')}
                        </Button>
                    )}
                </Stack>
                <Grid container spacing={1.5}>
                    {fields.map((field) => (
                        <Grid item xs={12} md={field.md ?? 12} key={field.key}>
                            {renderAddressField(field, isTax)}
                        </Grid>
                    ))}
                    {isTax && isSpain ? (
                        <>
                            <Grid item xs={12}>
                                <CommonInput
                                    inputType="INPUT"
                                    fullWidth
                                    label="restaurant_info_page_text_district"
                                    size="small"
                                    disabled={isPreview}
                                    value={restaurantInfoState.editRestaurantData.reg_municipality || ''}
                                    onChange={(event) => updateField('reg_municipality', event.target.value)}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="start">
                                                <ButtonComponent
                                                    variant='text'
                                                    handleClick={() => restaurantInfoState.searchMunicipalityByPostcode(
                                                        'reg_postcode',
                                                        'reg_municipality',
                                                    )}
                                                    titleText={i18n.t('restaurant_info_page_text_search_municipality_by_postcode')}
                                                    sx={{ marginRight: -6 }}
                                                >
                                                    <SearchIcon />
                                                </ButtonComponent>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonInput
                                    inputType="INPUT"
                                    fullWidth
                                    label="restaurant_info_page_text_number"
                                    size="small"
                                    disabled={isPreview}
                                    value={restaurantInfoState.editRestaurantData.reg_street_number || ''}
                                    onChange={(event) => updateField('reg_street_number', event.target.value)}
                                    inputProps={{ minLength: 1, maxLength: 20 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonInput
                                    inputType="SELECT"
                                    fullWidth
                                    label="restaurant_info_page_text_region"
                                    size="small"
                                    disabled={isPreview}
                                    items={SPAIN_CITY_AREA}
                                    value={restaurantInfoState.editRestaurantData.reg_territory || ''}
                                    onChange={(event) => updateField('reg_territory', event.target.value)}
                                />
                            </Grid>
                        </>
                    ) : null}
                </Grid>
            </Box>
        );
    };

    return (
        <Stack className="restaurant-dual-address" spacing={3}>
            <Box className="restaurant-dual-address__columns">
                {renderAddressColumn('business')}
                {renderAddressColumn('tax')}
            </Box>

            <Stack
                className={`restaurant-dual-address__same${restaurantInfoState.isAddressSame ? ' restaurant-dual-address__same--on' : ''}`}
                direction="row"
                alignItems="center"
                flexWrap="wrap"
                spacing={1}
            >
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={restaurantInfoState.isAddressSame}
                            disabled={isPreview}
                            onChange={(event) => restaurantInfoState.setAddressSame(event.target.checked)}
                        />
                    )}
                    label={(
                        <Typography variant="subtitle2">
                            {i18n.t('archive_form_same_address')}
                        </Typography>
                    )}
                />
                <Typography className="restaurant-dual-address__same-hint" variant="caption" color="text.secondary">
                    {i18n.t(
                        restaurantInfoState.isAddressSame
                            ? 'archive_form_same_address_locked_hint'
                            : 'archive_form_same_address_unlocked_hint',
                    )}
                </Typography>
            </Stack>

            {differences.length > 0 ? (
                <Typography
                    className="restaurant-dual-address__difference"
                    variant="body2"
                    color="warning.main"
                >
                    {i18n.t('archive_form_address_difference', {
                        count: differences.length,
                        fields: differences.map((item) => i18n.t(item.label)).join('、'),
                    })}
                </Typography>
            ) : null}
            {!restaurantInfoState.isAddressSame && differences.length === 0 ? (
                <Typography
                    className="restaurant-dual-address__equal"
                    variant="body2"
                    color="success.main"
                >
                    {i18n.t('archive_form_address_equal_hint')}
                </Typography>
            ) : null}
        </Stack>
    );
}
